import React, { useState, useEffect } from "react"
import cx from "classnames"
import Button, { IconButton, ButtonGroup } from "@kit/design/button"
import { Link } from "core/link"
import { motion, AnimatePresence } from "framer-motion"
import useQueryParams from "../../hooks/use-query-params"
import useHover from "../../hooks/use-hover"
import useScreenDimensions from "../../hooks/use-screen-dimensions"
import useLocalStorage from "hooks/use-local-storage"
import MobilePreviewFrame from "./assets/mobile-preview-frame.svg"
import Icon from "@kit/design/icon"
import useCurrentUser from "hooks/use-current-user"
import { useCreatePage } from "sdk/pages/mutations"
import { navigate } from "core/navigation"

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)

const BREAKPOINT = 992

function TemplateItem({ template, active }) {
  const [hoverProps, hovered] = useHover()
  const { width } = useScreenDimensions()

  const className = cx(
    "border border-gray-200 rounded overflow-hidden block bg-blue-800 flex-1 w-56",
    {
      shadow: hovered || active,
      "mr-2": width < BREAKPOINT,
      "mb-4": width >= BREAKPOINT
    }
  )

  const imageClassName = cx("opacity-50", {
    "opacity-100 shadow": hovered || active
  })

  return (
    <Link
      {...hoverProps}
      enabled={true}
      className={className}
      href={template.preview_path}
      key={template.preview_path}
    >
      <img
        className={imageClassName}
        style={{ transition: "opacity 300ms ease-in-out" }}
        alt="" // not an important image for assistive technology
        loading="lazy"
        src={template.screenshot}
      />
    </Link>
  )
}

function TemplateSidebar({
  templates,
  selectedTemplate,
  toggleTemplates,
  favorites,
  showOnlyFavorites
}) {
  const { width } = useScreenDimensions()
  const templatesWidth = width < BREAKPOINT ? templates.length * 160 : 230
  const sidebarClassNames = cx("p-4 bg-gray-50 ", {
    "border-t fixed bottom-0 overflow-x-scroll w-full z-10 h-32":
      width < BREAKPOINT,
    "block overflow-y-scroll overflow-x-hidden": width >= BREAKPOINT
  })

  return (
    <div className={sidebarClassNames}>
      <div
        className={width < BREAKPOINT ? "inline-flex flex-1 absolute" : ""}
        style={{ width: `${templatesWidth}px` }}
      >
        {(width > BREAKPOINT && favorites.length) > 0 && (
          <div className="h-8 text-md text-gray-700 sticky top-0 flex items-center py-4 pl-1">
            <button
              className={`mr-4 focus:outline-none focus:shadow-outline ${
                !showOnlyFavorites ? "font-semibold" : ""
              }`}
              aria-label="view all templates"
              onClick={() => toggleTemplates(false)}
            >
              All
            </button>
            <button
              className={`focus:outline-none focus:shadow-outline ${
                showOnlyFavorites ? "font-semibold" : ""
              }`}
              aria-label="view your favorite templates"
              onClick={() => toggleTemplates(true)}
            >
              Favorites
            </button>
          </div>
        )}
        {templates.map(template => (
          <TemplateItem
            key={template.preview_path}
            template={template}
            active={selectedTemplate.name == template.name}
          />
        ))}
      </div>
    </div>
  )
}

export const Frame = ({ src, view }) => {
  const { width } = useScreenDimensions()
  const isMobile = view === "mobile"

  const previewStyle = {
    borderRadius: isMobile ? "23px" : 0,
    zIndex: 2
  }

  const previewFrameStyle = {
    transform: isMobile && width < 766 ? "scale(.9)" : null
  }

  const easing = [0.5, 0, 0.5, 1]

  return (
    <div
      className="flex justify-center items-center h-full relative mb-24 md:mb-0 transition-transform duration-75 ease-in-out"
      style={previewFrameStyle}
    >
      <AnimatePresence initial={false}>
        <motion.iframe
          src={src}
          key={view}
          style={previewStyle}
          className="absolute"
          width="100%"
          initial={{ opacity: 0, scale: 0 }}
          exit={{ opacity: 0 }}
          animate={{
            opacity: 1,
            scale: isMobile ? 0.8 : 1,
            width: isMobile ? "380px" : "100%",
            height: isMobile ? "775px" : "100%"
          }}
          transition={{
            type: "tween",
            ease: easing,
            duration: 0.35
          }}
          title="Landing page preview"
        />
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {isMobile && (
          <motion.span
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ type: "tween", ease: easing, duration: 1.5 }}
            key={view}
          >
            <MobilePreviewFrame
              role="img"
              aria-label="Mobile Phone Emulator"
              data-testid="mobile-frame"
              style={{
                zIndex: 1,
                height: "668px",
                width: "331px",
                transform: "scale(1.13)"
              }}
            />
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}

function PagesPreview({ templates, template, flavor }) {
  const query = useQueryParams()
  const selectedCategory = query.get("category")
  const { width } = useScreenDimensions()
  const [view, setView] = useState("desktop")
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false)
  const [favorites, setFavorite] = useLocalStorage("favorite-templates", [])
  const isFavorite = favorites.includes(template.name)
  const { anonymous } = useCurrentUser()

  useEffect(() => {
    if (favorites.length === 0) {
      setShowOnlyFavorites(false)
    }
  }, [favorites])

  const visibleTemplates = selectedCategory
    ? templates.filter(
        t => t.categories.includes(selectedCategory) && t.name !== template.name
      )
    : showOnlyFavorites === false
    ? templates
    : templates.filter(t => favorites.includes(t.name))

  const handleFavoriteClick = () => {
    isFavorite
      ? setFavorite(favorites.filter(fav => template.name != fav))
      : setFavorite([...favorites, template.name])
  }

  const toggleTemplates = showOnlyFavorites => {
    setShowOnlyFavorites(showOnlyFavorites)
  }

  const { mutate: createPage } = useCreatePage({
    onSuccess: data => {
      navigate(data.redirect)
    },
    onError: error => {
      console.error(error)
    }
  })

  const handleSelectionClick = () => {
    createPage({ template, iframe: window.top !== window })
  }

  return (
    <div className="w-full flex h-screen absolute top-0 bg-gray-50">
      <TemplateSidebar
        templates={visibleTemplates}
        selectedTemplate={template}
        toggleTemplates={toggleTemplates}
        favorites={favorites}
        showOnlyFavorites={showOnlyFavorites}
      />
      <div className="bg-gray-200 flex-grow shadow flex flex-col">
        <div
          className="shadow p-4 flex items-center bg-white"
          style={{ minHeight: "70px" }}
        >
          <Link href="/pages/new">
            <IconButton
              is="tertiary"
              icon="left"
              label="back to template selection"
            />
          </Link>
          <h1 className="font-bold text-2xl flex-grow">
            {capitalize(template.name)}
          </h1>
          <div className="flex">
            {width > BREAKPOINT && (
              <>
                <IconButton
                  className={cx("mr-2", {
                    "text-red-500": isFavorite,
                    "text-gray-800": !isFavorite
                  })}
                  active={isFavorite}
                  icon={isFavorite ? "heart-filled" : "heart"}
                  is="secondary"
                  label="Favorite template"
                  onClick={handleFavoriteClick}
                />
                <ButtonGroup>
                  <IconButton
                    active={view === "mobile"}
                    icon="device-mobile"
                    data-testid="mobile-btn"
                    label="Preview mobile view"
                    onClick={() => setView("mobile")}
                  />
                  <IconButton
                    active={view === "desktop"}
                    icon="device-desktop"
                    data-testid="desktop-btn"
                    label="Preview desktop view"
                    onClick={() => setView("desktop")}
                  />
                </ButtonGroup>
                <Button
                  href={`/pages/preview/${template.name}/${flavor}`}
                  is="tertiary"
                  className="uppercase ml-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                  <Icon name="external" className="ml-2 align-middle" />
                </Button>
              </>
            )}
            {anonymous ? (
              <Button
                href={`/pages/create?template=${template.name}`}
                className="ml-2 uppercase font-semibold"
                is="primary"
              >
                Choose
              </Button>
            ) : (
              <Button
                onClick={handleSelectionClick}
                className="ml-2 uppercase font-semibold"
                is="primary"
              >
                Choose
              </Button>
            )}
          </div>
        </div>
        <Frame src={`/pages/preview/${template.name}/${flavor}`} view={view} />
      </div>
    </div>
  )
}

export default PagesPreview
